import React, { useState } from "react";
import { Button, Modal, Table, Badge, InputGroup } from "react-bootstrap";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconTrash,
  IconBuildingWarehouse,
} from "@tabler/icons-react";
import { useWarehouse } from "../context/WarehouseContext";
import WarehouseForm from "./WarehouseForm";
import Swal from "sweetalert2";

const WarehouseManagement = () => {
  const {
    warehouses,
    loading,
    refetch,
    handleSearch,
    // createWarehouse,
    // updateWarehouse,
    deleteWarehouse,
    warehouseStats,
    Pagination,
  } = useWarehouse();

  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);

  const handleCreateWarehouse = () => {
    setSelectedWarehouse(null);
    setShowWarehouseModal(true);
  };

  const handleEditWarehouse = (warehouse) => {
    setSelectedWarehouse(warehouse);
    setShowWarehouseModal(true);
  };

  const handleDeleteWarehouse = async (warehouse) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete warehouse "${warehouse.name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteWarehouse(warehouse.id);
        Swal.fire("Deleted!", "Warehouse has been deleted.", "success");
      } catch (error) {
        Swal.fire("Error!", "Failed to delete warehouse.", "error");
      }
    }
  };

  const handleWarehouseModalClose = () => {
    setShowWarehouseModal(false);
    setSelectedWarehouse(null);
  };

  const handleWarehouseFormSuccess = () => {
    setShowWarehouseModal(false);
    setSelectedWarehouse(null);
    refetch();
    Swal.fire("Success!", "Warehouse has been saved.", "success");
  };

  const handleWarehouseFormError = (error) => {
    Swal.fire("Error!", error.message || "Failed to save warehouse.", "error");
  };

  const getTypeBadgeVariant = (type) => {
    switch (type) {
      case "central":
        return "primary";
      case "field":
        return "success";
      case "regional":
        return "info";
      case "temporary":
        return "warning";
      default:
        return "secondary";
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "central":
        return "Central";
      case "field":
        return "Field";
      case "regional":
        return "Regional";
      case "temporary":
        return "Temporary";
      default:
        return type;
    }
  };

  return (
    <>
      <div style={{ marginBottom: "32px" }}>
        {/* Warehouse Statistics */}
        <div style={{ display: "flex", gap: "16px", marginBottom: "24px" }}>
          <div
            style={{
              flex: 1,
              backgroundColor: "#e3f2fd",
              padding: "16px",
              borderRadius: "8px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <IconBuildingWarehouse size={32} style={{ color: "#1976d2" }} />
              <div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: "#1976d2",
                  }}
                >
                  {warehouseStats.loading ? (
                    <div className="spinner-border spinner-border-sm" />
                  ) : (
                    warehouseStats.total || warehouses.length
                  )}
                </div>
                <div style={{ fontSize: "14px", color: "#424242" }}>
                  Total Warehouses
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                color: "#374151",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <IconBuildingWarehouse size={24} />
              Warehouse Management
            </h3>
            <p
              style={{
                margin: "4px 0 0 0",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              Manage warehouse locations and information
            </p>
          </div>
          <Button
            variant="primary"
            onClick={handleCreateWarehouse}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <IconPlus size={16} />
            Add Warehouse
          </Button>
        </div>

        {/* Warehouse Search */}
        <div style={{ marginBottom: "16px" }}>
          <InputGroup style={{ maxWidth: "400px" }}>
            <InputGroup.Text>
              <IconSearch size={16} />
            </InputGroup.Text>
            <div
              className="form-control"
              style={{ border: "none", padding: 0 }}
            >
              <input
                type="text"
                className="form-control border-0"
                placeholder="Search warehouses..."
                onKeyPress={handleSearch}
                style={{ boxShadow: "none" }}
              />
            </div>
          </InputGroup>
        </div>

        {/* Warehouse Table */}
        <div className="card">
          <div className="card-body" style={{ padding: 0 }}>
            <Table striped bordered hover style={{ marginBottom: 0 }}>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Address</th>
                  <th>Manager</th>
                  <th>Status</th>
                  <th style={{ textAlign: "center", width: "120px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="7"
                      style={{ textAlign: "center", padding: "40px" }}
                    >
                      <div className="spinner-border" />
                      <div style={{ marginTop: "8px" }}>
                        Loading warehouses...
                      </div>
                    </td>
                  </tr>
                ) : warehouses?.length > 0 ? (
                  warehouses.map((warehouse) => (
                    <tr key={warehouse.id}>
                      <td>
                        <code>{warehouse.code}</code>
                      </td>
                      <td>
                        <div style={{ fontWeight: "500", fontSize: "16px" }}>
                          {warehouse.name}
                        </div>
                        {warehouse.description && (
                          <small className="text-muted">
                            {warehouse.description.length > 50
                              ? `${warehouse.description.substring(0, 50)}...`
                              : warehouse.description}
                          </small>
                        )}
                      </td>
                      <td>
                        <Badge bg={getTypeBadgeVariant(warehouse.type)} text="white">
                          {getTypeLabel(warehouse.type)}
                        </Badge>
                      </td>
                      <td>
                        <div style={{ maxWidth: "300px" }}>
                          {warehouse.address.length > 50
                            ? `${warehouse.address.substring(0, 50)}...`
                            : warehouse.address}
                        </div>
                      </td>
                      <td>{warehouse.manager_id || "-"}</td>
                      <td>
                        <Badge bg={warehouse.is_active ? "success" : "danger"} text="white">
                          {warehouse.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div
                          style={{
                            display: "flex",
                            gap: "4px",
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEditWarehouse(warehouse)}
                            title="Edit Warehouse"
                          >
                            <IconEdit size={14} />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteWarehouse(warehouse)}
                            title="Delete Warehouse"
                          >
                            <IconTrash size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="7"
                      style={{ textAlign: "center", padding: "40px" }}
                    >
                      <div style={{ textAlign: "center" }}>
                        <IconBuildingWarehouse
                          size={48}
                          className="text-muted mb-3"
                        />
                        <h5 className="text-muted">No warehouses found</h5>
                        <p className="text-muted">
                          Start by creating your first warehouse to organize
                          inventory locations.
                        </p>
                        <Button
                          variant="primary"
                          onClick={handleCreateWarehouse}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            margin: "0 auto",
                          }}
                        >
                          <IconPlus size={16} />
                          Create First Warehouse
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* Warehouse Pagination */}
          <div className="d-flex justify-content-center mt-3">{Pagination}</div>
        </div>
      </div>

      {/* Warehouse CRUD Modal */}
      <Modal
        show={showWarehouseModal}
        onHide={handleWarehouseModalClose}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedWarehouse ? "Edit Warehouse" : "Create New Warehouse"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <WarehouseForm
            initialData={selectedWarehouse}
            onSuccess={handleWarehouseFormSuccess}
            onError={handleWarehouseFormError}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default WarehouseManagement;
